/**
 * Dialog composable for global confirm/alert dialogs
 *
 * Usage:
 *   const { confirm, alert } = useDialog()
 *
 *   // Confirm dialog (returns true/false)
 *   const confirmed = await confirm('Are you sure you want to delete this?', {
 *     title: 'Delete Item',
 *     confirmText: 'Delete',
 *     confirmColor: 'error',
 *   })
 *
 *   // Alert dialog (just acknowledgment)
 *   await alert('Operation completed successfully', { title: 'Success' })
 */

// Global dialog state
const dialogState = ref({
  visible: false,
  message: '',
  title: '',
  confirmText: 'OK',
  cancelText: 'Cancel',
  confirmColor: 'primary',

  /** Header icon — see `iconFor` for the per-colour default. */
  icon: '',
  showCancel: true,
  onConfirm: null,
  onCancel: null,
})

/**
 * The header icon a dialog wears when the caller doesn't name one (Sandy
 * 2026-08-18: "que se vea como los demás"). Derived from the confirm colour,
 * which is what every call site already sets: destructive asks with a warning
 * triangle, everything else with a question.
 */
function iconFor(color) {
  if (color === 'error' || color === 'warning') return 'tabler-alert-triangle'
  if (color === 'success') return 'tabler-circle-check'

  return 'tabler-help-circle'
}

export function useDialog() {
  /**
   * Open a confirm dialog
   * @param {string} message - Dialog message
   * @param {Object} options - Dialog options
   * @param {string} options.title - Dialog title
   * @param {string} options.confirmText - Confirm button text
   * @param {string} options.cancelText - Cancel button text
   * @param {string} options.confirmColor - Confirm button color
   * @param {string} options.icon - Header icon (defaults to one per colour)
   * @returns {Promise<boolean>} - Resolves to true if confirmed, false if cancelled
   */
  function confirm(message, options = {}) {
    return new Promise(resolve => {
      dialogState.value = {
        visible: true,
        message,
        title: options.title || 'Confirm',
        confirmText: options.confirmText || 'OK',
        cancelText: options.cancelText || 'Cancel',
        confirmColor: options.confirmColor || 'primary',
        icon: options.icon || iconFor(options.confirmColor),
        showCancel: true,
        onConfirm: () => {
          dialogState.value.visible = false
          resolve(true)
        },
        onCancel: () => {
          dialogState.value.visible = false
          resolve(false)
        },
      }
    })
  }

  /**
   * Open an alert dialog (no cancel button)
   * @param {string} message - Dialog message
   * @param {Object} options - Dialog options
   * @returns {Promise<void>}
   */
  function alert(message, options = {}) {
    return new Promise(resolve => {
      dialogState.value = {
        visible: true,
        message,
        title: options.title || '',
        confirmText: options.confirmText || 'OK',
        cancelText: '',
        confirmColor: options.confirmColor || 'primary',
        icon: options.icon || iconFor(options.confirmColor),
        showCancel: false,
        onConfirm: () => {
          dialogState.value.visible = false
          resolve()
        },
        onCancel: () => {
          dialogState.value.visible = false
          resolve()
        },
      }
    })
  }

  /**
   * Open a delete confirmation dialog (pre-configured with danger styling)
   * @param {string} itemName - Name of the item being deleted
   * @param {Object} options - Additional options
   * @returns {Promise<boolean>}
   */
  function confirmDelete(itemName, options = {}) {
    return confirm(
      options.message || `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      {
        title: options.title || 'Delete Confirmation',
        confirmText: options.confirmText || 'Delete',
        cancelText: options.cancelText || 'Cancel',
        confirmColor: 'error',
      },
    )
  }

  /**
   * Close the dialog programmatically
   */
  function closeDialog() {
    dialogState.value.visible = false
  }

  /**
   * Legacy method for backward compatibility
   * @deprecated Use confirm() instead
   */
  function openDialog(message, options = {}) {
    return confirm(message, options)
  }

  return {
    // State
    dialogState,

    // Methods
    confirm,
    alert,
    confirmDelete,
    closeDialog,

    // Legacy (deprecated)
    openDialog,
  }
}
